import { LitElement, html } from "lit";

import { customElement, state } from "lit/decorators.js";
import { createRef, ref } from "lit/directives/ref.js";

import "@phosphor-icons/webcomponents/PhCaretUp";
import { appContext } from "ui-common/appContext";
import { DataQueryController } from "ui-common/data-query-controller";
import "ui-components/button/wow-button";
import "ui-components/confirmation/wow-confirmation";
import "ui-components/group-button/group-button";
import { NotificationService } from "ui-components/notification/notification-service";

import { styles } from "./worlds-page.css";

import "../../components/page-content/page-content";
import "../../components/world-card/world-card";
import type { WorldEditData } from "../../components/world-form-modal/world-form-modal";
import "../../components/world-form-modal/world-form-modal";
import "../../components/worlds-layout/worlds-layout";
import "../../components/preview-profile";
import type { PreviewProfile } from "../../components/preview-profile/preview-profile";
import { globalStyles } from "../../components/worlds-layout/worlds-layout-global.css";
import { worldsQueryOptions } from "../../queries/worlds";
import { navigateTo } from "../../router";
import { createWorldMutation, deleteWorldMutation, updateWorldMutation } from "../../services/world-mutations";
import { t } from "../../translations/i18n";

type WorldFilter = "all" | "live" | "draft";

@customElement("worlds-page")
export class WorldsPage extends LitElement {
  static override styles = [styles, globalStyles];

  @state()
  private selectedFilter: WorldFilter = "live";

  @state()
  private isCreateModalOpen = false;

  @state()
  private modalMode: "create" | "edit" = "create";

  @state()
  private editingWorldData?: WorldEditData;

  @state()
  private isDeleteConfirmationOpen = false;

  @state()
  private worldToDelete?: { id: string; name: string };

  private worldsQuery = new DataQueryController(this, () => worldsQueryOptions);
  private previewProfileRef = createRef<PreviewProfile>();

  private handleFilterChange(filter: WorldFilter) {
    this.selectedFilter = filter;
  }

  private handleCreateWorld = () => {
    this.modalMode = "create";
    this.editingWorldData = undefined;
    this.isCreateModalOpen = true;
  };

  private handleCloseCreateModal = () => {
    this.isCreateModalOpen = false;
    this.editingWorldData = undefined;
  };

  private handleWorldEdit = (event: CustomEvent) => {
    const { worldData } = event.detail;
    this.modalMode = "edit";
    this.editingWorldData = worldData;
    this.isCreateModalOpen = true;
  };

  private handleWorldDelete = (event: CustomEvent) => {
    const { worldId, worldName } = event.detail;
    this.worldToDelete = { id: worldId, name: worldName };
    this.isDeleteConfirmationOpen = true;
  };

  private handleDeleteConfirm = async () => {
    if (!this.worldToDelete) return;

    try {
      await deleteWorldMutation.mutate({ worldId: this.worldToDelete.id });
      NotificationService.success("World deleted successfully!");
      this.worldsQuery.invalidate();
    } catch (error) {
      NotificationService.error("Failed to delete world. Please try again.");
      console.error("Failed to delete world:", error);
    } finally {
      this.isDeleteConfirmationOpen = false;
      this.worldToDelete = undefined;
    }
  };

  private handleDeleteCancel = () => {
    this.isDeleteConfirmationOpen = false;
    this.worldToDelete = undefined;
  };

  private handleWorldSave = async (event: CustomEvent) => {
    const { mode, data } = event.detail;

    try {
      if (mode === "create") {
        // Create the world using the mutation
        await createWorldMutation.mutate({
          name: data.name,
          description: undefined, // Not supported yet
          thumbnailFileId: data.thumbnailFileId || undefined,
          postIds: data.postIds,
        });

        if (data.postIds.length === 0) this.selectedFilter = "draft";

        // Show success notification
        NotificationService.success("World created successfully!");
      } else {
        // Update the world using the mutation
        const updateParams: {
          id: string;
          name: string;
          description?: string;
          thumbnailFileId?: string;
          postIds?: string[];
        } = {
          id: data.id,
          name: data.name,
          postIds: data.postIds,
        };

        if (data.thumbnailFileId != undefined && data.thumbnailFileId !== "undefined") {
          updateParams.thumbnailFileId = data.thumbnailFileId;
        }

        await updateWorldMutation.mutate(updateParams);

        // Show success notification
        NotificationService.success("World updated successfully!");
      }

      // Close the modal on success
      this.isCreateModalOpen = false;

      // Refresh the worlds list to show changes
      this.worldsQuery.invalidate();

      // Reload the preview iframe
      this.previewProfileRef.value?.reloadIframe();
    } catch (error) {
      if (((error as Error)?.message ?? "").includes("duplicate key")) {
        NotificationService.error(`The world with the same name already exists.`);
      } else {
        // Show error notification
        const action = mode === "create" ? "create" : "update";
        NotificationService.error(`Failed to ${action} world. Please try again.`);
        console.error(`Failed to ${action} world:`, error);
      }
      // Keep the modal open so user can try again
    }
  };

  private handleOpenWorlds = () => {
    const handle = appContext.currentUser?.handle;

    if (handle) window.open(`/${handle}/my-world`, "_self", "noopener,noreferrer");
  };

  private renderFilterButtons() {
    const { data: worldsData } = this.worldsQuery.result;
    const counts = worldsData?.counts || { live: 0, draft: 0 };

    return html`
      <div class="filter-buttons">
        <wow-button
          variant=${this.selectedFilter === "live" ? "primary" : "subtle"}
          size="small"
          @click=${() => this.handleFilterChange("live")}
        >
          ${t("worlds.live")} (${counts.live})
        </wow-button>
        <wow-button
          variant=${this.selectedFilter === "draft" ? "primary" : "subtle"}
          size="small"
          @click=${() => this.handleFilterChange("draft")}
        >
          ${t("worlds.drafts")} (${counts.draft})
        </wow-button>
      </div>
    `;
  }

  private renderWorldsGrid() {
    const { data: worldsData, isPending, error } = this.worldsQuery.result;

    if (isPending) {
      return html`<div class="worlds-grid">
        <skeleton-rect height="148px"></skeleton-rect>
        <skeleton-rect height="148px"></skeleton-rect>
        <skeleton-rect height="148px"></skeleton-rect>
        <skeleton-rect height="148px"></skeleton-rect>
      </div>`;
    }

    if (error) {
      return html`<div class="error">Error loading worlds: ${error.message}</div>`;
    }

    const filteredWorlds =
      this.selectedFilter === "all"
        ? worldsData.worlds
        : worldsData.worlds.filter((world) => world.status === this.selectedFilter);

    return html`
      <div class="worlds-grid" @world-edit=${this.handleWorldEdit} @world-delete=${this.handleWorldDelete}>
        ${filteredWorlds.map(
          (world) => html`
            <world-card
              .id=${world.id}
              .name=${world.name}
              .mediaUrl=${world.mediaUrl ?? undefined}
              .mediaPositionX=${world.mediaPositionX ?? undefined}
              .mediaPositionY=${world.mediaPositionY ?? undefined}
              .postCount=${world.postCount}
              .status=${world.status}
              .showDelete=${true}
            >
            </world-card>
          `,
        )}
      </div>
    `;
  }

  private renderEmptyState() {
    return html`
      <div class="empty-state">
        <h2 class="empty-state-title">${t("worlds.emptyStateTitle")}</h2>
        <p class="empty-state-subtitle">${t("worlds.emptyStateSubtitle")}</p>
        <wow-button variant="neutral" size="medium" @click=${this.handleCreateWorld}>
          ${t("worlds.createWorld")}
        </wow-button>
      </div>
    `;
  }

  private renderMainContent() {
    const { data: worldsData, isPending } = this.worldsQuery.result;

    if (!isPending && !worldsData?.worlds?.length) {
      return html` <div class="main-content">${this.renderEmptyState()}</div> `;
    }

    return html`<div class="main-content">${this.renderFilterButtons()} ${this.renderWorldsGrid()}</div>`;
  }

  protected override render() {
    return html`
      <page-content>
        <!-- Navigation buttons slot -->
        <div slot="title">
          <group-button>
            <wow-button variant="primary" size="small">Worlds</wow-button>
            <wow-button variant="subtle" size="small" @click=${() => navigateTo("worlds/posts")}>Posts</wow-button>
          </group-button>
        </div>

        <!-- Action buttons slot -->
        <div slot="actions" class="action-buttons">
          <view-primary-buttons
            @primary-click=${this.handleCreateWorld}
            @view-click=${this.handleOpenWorlds}
          ></view-primary-buttons>
        </div>

        <section class="container">
          <worlds-layout>
            <!-- Main content slot -->
            <div slot="main-content">${this.renderMainContent()}</div>

            <!-- Secondary content slot -->
            <div slot="secondary-content">
              <div class="info">
                <h3 class="secondary-title">Create & Organize</h3>
                <p class="secondary-description">
                  Organize your content into themed worlds. Each world can contain multiple posts and helps you
                  structure your content for better audience engagement.
                </p>
              </div>

              <preview-profile page="my-world" ${ref(this.previewProfileRef)}></preview-profile>
            </div>
          </worlds-layout>

          <!-- World Form Modal -->
          ${this.isCreateModalOpen
            ? html`
                <world-form-modal
                  .mode=${this.modalMode}
                  .data=${this.editingWorldData}
                  @modal-close=${this.handleCloseCreateModal}
                  @world-save=${this.handleWorldSave}
                ></world-form-modal>
              `
            : ""}

          <!-- Delete Confirmation Dialog -->
          ${this.isDeleteConfirmationOpen && this.worldToDelete
            ? html`
                <wow-confirmation header=${t("worlds.deleteDialogHeader")}>
                  <div class="unsaved-changes-content">
                    <p>${t("worlds.deleteConfirmation")} "${this.worldToDelete.name}"?</p>
                    <p>${t("worlds.deleteWarning")}</p>
                    <wow-button class="danger" variant="neutral" size="medium" @click=${this.handleDeleteConfirm}>
                      ${t("worlds.deleteButtonText")}
                    </wow-button>
                    <wow-button variant="neutral" size="medium" @click=${this.handleDeleteCancel}>
                      ${t("deleteLink.cancel")}
                    </wow-button>
                  </div>
                </wow-confirmation>
              `
            : ""}
        </section>
      </page-content>
    `;
  }
}
